export type DirectoryType = "google" | "azure" | "okta" | "ldap" | "proxy";

export type DirectoryStatus = "pending" | "healthy" | "degraded" | "auth_failed" | "sync_failed";

export interface GoogleDirectoryConfig {
    hasCredential: boolean
    hasToken: boolean
    // The file this directory runs on, and one uploaded but not consented to yet.
    // A staged upload never displaces the granted one until Google says so.
    credentialsName?: string
    pendingCredentialsName?: string
    consentUrl?: string
}

export interface AzureDirectoryConfig {
    tenantId: string
    clientId: string
    clientSecret: string
}

export interface OktaDirectoryConfig {
    apiDomain: string
    apiToken: string
}

export interface LdapDirectoryConfig {
    addr: string
    username: string
    password: string
    domain?: string
    // The domain controller this forest domain row is read through.
    domainController?: string
}

export interface LdapDomainController {
    // Empty on a DC with no DNS host name recorded, which cannot be dialled.
    host: string
    // The controller's IPv4 address as the directory itself records it. Absent
    // when the directory holds no readable DNS zone for this host.
    address?: string
    name: string
    operatingSystem?: string
    distinguishedName?: string
}

export interface DomainControllerDiscovery {
    domain: string
    controllers: LdapDomainController[]
    configured: string
}

// What a domain row stores for a listed controller. On ldaps it is the host
// name, since the certificate names the host and an address would fail its
// check; otherwise the address, so reaching it needs no DNS, falling back to the
// host name when no address is known. Empty when it can't be dialled.
export function controllerEndpoint(dc: LdapDomainController, forestAddr: string): string {
    if (forestAddr.trim().toLowerCase().startsWith("ldaps://")) {
        return dc.host;
    }
    return dc.address || dc.host;
}

interface DirectoryBase {
    id: string
    name: string
    description: string
    enabled: boolean
    status: DirectoryStatus
    connectionValidated: boolean
    connectionValidationError?: string
    createdAt: string
    updatedAt: string
    parentId?: string
    // Shield nodes (by name) whose traffic may look users up here. Empty means every node.
    icapInstances?: string[]
    // A forest domain row's own switch; `enabled` is whether it runs, which also
    // needs the forest's switch on. Absent on other rows.
    domainEnabled?: boolean
}

export type Directory =
    | (DirectoryBase & { type: "google", config: GoogleDirectoryConfig })
    | (DirectoryBase & { type: "azure", config: AzureDirectoryConfig })
    | (DirectoryBase & { type: "okta", config: OktaDirectoryConfig })
    | (DirectoryBase & { type: "ldap", config: LdapDirectoryConfig })
    // The built-in Proxy Directory: no external provider, so no config block.
    // System-seeded, never created or edited through this wizard.
    | (DirectoryBase & { type: "proxy" })

export type DirectoryConfigInput =
    | GoogleDirectoryConfig
    | (Omit<AzureDirectoryConfig, "clientSecret"> & { clientSecret?: string })
    | (Omit<OktaDirectoryConfig, "apiToken"> & { apiToken?: string })
    | (Omit<LdapDirectoryConfig, "password"> & { password?: string });

export const maskedSecret = "**********";

export const directoryDisplayNames: { [key in DirectoryType]: string } = {
    "google": "Google Workspace",
    "azure": "Microsoft Entra ID",
    "okta": "Okta",
    "ldap": "Microsoft Active Directory",
    "proxy": "Proxy Directory",
};

export function directoryDisplayName(value: string): string {
    const type = value.replace(/_directory$/, "");
    return type in directoryDisplayNames ? directoryDisplayNames[type as DirectoryType] : value;
}

export const directoryDescriptions: { [key in DirectoryType]: string } = {
    "google": "Syncs users and groups from Google Workspace over the directory admin API.",
    "azure": "Syncs users and groups from Microsoft Entra ID (formerly Azure AD) over the cloud API.",
    "okta": "Syncs users and groups from an Okta organization over the Okta API.",
    "ldap": "Syncs users and groups from an on-premises Active Directory over LDAP.",
    "proxy": "Local directory for groups with no external provider behind them.",
};

export function hasChildren(directory: Directory, all: Directory[]): boolean {
    return all.some((d) => d.parentId === directory.id);
}

// A forest domain row's own switch. Its `enabled` is whether it runs, which
// also needs the forest's switch on.
export function ownEnabled(directory: Directory): boolean {
    return directory.parentId !== undefined
        ? (directory.domainEnabled ?? directory.enabled)
        : directory.enabled;
}

// What the Enabled column shows. A domain that is on but not running is held
// back by its forest, which is worth saying rather than plain "Disabled".
export function enabledLabel(directory: Directory): { label: string, running: boolean } {
    if (directory.enabled) {
        return { label: "Enabled", running: true };
    }
    if (directory.parentId !== undefined && ownEnabled(directory)) {
        return { label: "Forest disabled", running: false };
    }
    return { label: "Disabled", running: false };
}

// A Google directory runs on credentials that have been consented to: one that has
// never been authorized cannot be switched on, and neither can one holding a file
// that was uploaded and is still waiting for its consent. The API says the same, with
// a 422, this only keeps the switch from being offered where it would be refused.
export function canBeEnabled(directory: Directory): boolean {
    return directory.type !== "google"
        || (directory.config.hasToken && directory.config.pendingCredentialsName === undefined);
}

// Groups are managed where they live: on a directory whose connection has been
// validated, and for LDAP only on a domain row - users are looked up domain by
// domain, never through the forest. The table's row action and the wizard both key
// "Manage groups" off this. The Proxy Directory has no live provider to browse
// groups from at all - its groups only ever show up on the general Groups screen.
export function canManageGroups(directory: Directory): boolean {
    return directory.type !== "proxy"
        && directory.connectionValidated
        && (directory.type !== "ldap" || directory.parentId !== undefined);
}
